/**
 * German: the default and the source of truth for keys. Every other locale must
 * have exactly the same shape (enforced by the `Messages` type in index.ts).
 * Interpolation uses i18next syntax: {{name}}. German UI uses informal "du".
 */
export const de = {
  app: {
    name: 'TicketAI',
    tagline: 'Tickets für Claude-Assistenten'
  },
  common: {
    loading: 'Wird geladen …',
    retry: 'Erneut versuchen',
    unknownError: 'Ein unerwarteter Fehler ist aufgetreten.',
    save: 'Speichern',
    saved: 'Gespeichert.',
    cancel: 'Abbrechen',
    close: 'Schließen',
    back: 'Zurück',
    copy: 'Kopieren',
    copied: 'Kopiert.',
    none: '–',
    loadMore: 'Mehr laden',
    all: 'Alle'
  },
  nav: {
    tickets: 'Tickets',
    approvals: 'Freigaben',
    assistants: 'Assistenten',
    log: 'Protokoll',
    account: 'Mein Konto',
    admin: 'Verwaltung',
    signOut: 'Abmelden',
    menu: 'Menü'
  },
  home: {
    status: 'Systemstatus',
    statusOk: 'Alle Dienste laufen',
    statusDegraded: 'Eingeschränkt: {{failed}} nicht erreichbar',
    statusUnreachable: 'Die API ist nicht erreichbar.',
    version: 'Version {{version}}'
  },
  checks: {
    database: 'Datenbank'
  },
  language: {
    label: 'Sprache',
    de: 'Deutsch',
    en: 'English'
  },
  notFound: {
    title: 'Seite nicht gefunden',
    back: 'Zur Startseite'
  },
  auth: {
    signInTitle: 'Anmelden',
    signInLead: 'Mit deinem TicketAI-Konto.',
    email: 'E-Mail',
    password: 'Passwort',
    signIn: 'Anmelden',
    or: 'oder',
    passkey: 'Mit Passkey anmelden',
    forgotPassword: 'Passwort vergessen?',
    invalidCredentials: 'E-Mail oder Passwort ist falsch.',
    tooManyAttempts: 'Zu viele Versuche. Bitte warte eine Minute.',
    passkeyFailed: 'Anmeldung mit Passkey hat nicht geklappt.',
    sessionExpired: 'Deine Sitzung ist abgelaufen. Bitte melde dich neu an.',
    forOAuth: 'Melde dich an, um Claude Code mit TicketAI zu verbinden.',
    forgotTitle: 'Passwort vergessen',
    forgotLead: 'Wir schicken dir einen Link, mit dem du ein neues Passwort festlegst.',
    sendLink: 'Link senden',
    linkSent: 'Wenn es ein Konto mit dieser Adresse gibt, ist der Link unterwegs.',
    resetTitle: 'Neues Passwort',
    newPassword: 'Neues Passwort',
    setPassword: 'Passwort festlegen',
    resetDone: 'Passwort geändert. Du kannst dich jetzt anmelden.',
    resetInvalid: 'Der Link ist ungültig oder abgelaufen.',
    passwordHint: 'Mindestens 12 Zeichen.',
    toSignIn: 'Zur Anmeldung',
    inviteTitle: 'Willkommen bei TicketAI',
    inviteLead: 'Du wurdest als {{email}} eingeladen. Lege deinen Namen und dein Passwort fest. Danach richtest du einen Passkey ein.',
    inviteInvalid: 'Diese Einladung ist ungültig, abgelaufen oder wurde schon verwendet. Bitte lass dir eine neue schicken.',
    name: 'Name',
    acceptInvite: 'Konto anlegen'
  },
  passkeySetup: {
    title: 'Passkey einrichten',
    lead: 'TicketAI braucht einen Passkey: Freigaben bestätigst du damit, nicht mit einem Klick.',
    why: 'Ein Assistent kann nichts freigeben, auch kein manipulierter. Nur du, mit deinem Gerät (Fingerabdruck, Gesicht oder PIN).',
    register: 'Passkey hinzufügen',
    failed: 'Der Passkey konnte nicht angelegt werden. Versuch es noch einmal.',
    defaultName: 'Mein Gerät'
  },
  status: {
    open: 'Offen',
    discussing: 'In Klärung',
    proposed: 'Vorschlag',
    approved: 'Freigegeben',
    in_progress: 'In Arbeit',
    done: 'Erledigt',
    closed: 'Abgeschlossen',
    declined: 'Abgelehnt',
    cancelled: 'Zurückgezogen',
    paused: 'Pausiert'
  },
  kind: {
    message: 'Nachricht',
    question: 'Frage',
    answer: 'Antwort',
    decline: 'Ablehnung',
    reopen: 'Wiedereröffnet',
    cancel: 'Zurückgezogen',
    close: 'Abgeschlossen',
    progress: 'Fortschritt',
    done_report: 'Erledigt-Bericht',
    request_human: 'Frage an den Menschen'
  },
  actor: {
    human: 'Mensch',
    assistant: 'Assistent',
    worker: 'Coding-Session',
    cli: 'CLI',
    system: 'System'
  },
  priority: {
    low: 'Niedrig',
    normal: 'Normal',
    high: 'Hoch'
  },
  tickets: {
    title: 'Tickets',
    lead: 'Alle Tickets aller Assistenten. Hier liest du nur: geschrieben wird von den Assistenten.',
    empty: 'Keine Tickets gefunden.',
    search: 'Suchen …',
    status: 'Status',
    mine: 'Nur meine',
    requester: 'Von',
    assignee: 'An',
    updated: 'Aktualisiert',
    needsHuman: 'Wartet auf einen Menschen',
    labels: 'Labels'
  },
  ticket: {
    request: 'Anfrage',
    thread: 'Verlauf',
    proposals: 'Vorschläge',
    decisions: 'Entscheidungen',
    notes: 'Notizen der Assistenten',
    log: 'Protokoll',
    noMessages: 'Noch keine Nachrichten.',
    noProposals: 'Noch kein Vorschlag.',
    noDecisions: 'Noch keine Entscheidung.',
    noNotes: 'Keine Notizen.',
    version: 'Version {{version}}',
    current: 'aktuell',
    changesSince: 'Änderungen gegenüber Version {{version}}',
    showDiff: 'Änderungen zeigen',
    showFull: 'Ganzen Text zeigen',
    diffTooLarge: 'Zu groß für eine Gegenüberstellung: der ganze aktuelle Text.',
    relaysHuman: 'laut Assistent von {{name}}',
    relaysHumanHint: 'Der Assistent gibt an, das von seinem Menschen weiterzugeben. Das ist nicht überprüft.',
    from: '{{name}} · {{actor}}',
    approve: 'Freigeben',
    reject: 'Ablehnen',
    pause: 'Pausieren',
    resume: 'Fortsetzen',
    note: 'Notiz (optional)',
    decideApprove: 'Version {{version}} freigeben',
    decideReject: 'Version {{version}} ablehnen',
    decidePause: 'Ticket pausieren',
    decideResume: 'Ticket fortsetzen',
    decideLead: 'Du bestätigst mit deinem Passkey. Die Freigabe gilt genau für diese Version (SHA-256 {{sha}}…). Ändert der Assistent den Vorschlag, braucht es eine neue Freigabe.',
    decidePauseLead: 'Du bestätigst mit deinem Passkey. Solange das Ticket pausiert ist, können die Assistenten nicht weiterschreiben.',
    confirm: 'Mit Passkey bestätigen',
    decisionFailed: 'Die Bestätigung hat nicht geklappt.',
    paused: 'Pausiert ({{reason}}). Die Assistenten können nicht weiterschreiben, bis ein Mensch fortsetzt.',
    relayStats: 'Vom Anfragenden: {{relayed}} von {{total}} Nachrichten gibt der Assistent als Aussage seines Menschen aus. Der Rest ist die eigene Formulierung des Assistenten.',
    approvedBy: 'Freigegeben von {{name}}',
    rejectedBy: 'Abgelehnt von {{name}}',
    forVersion: 'für Version {{version}}',
    waitingFor: 'Wartet auf eine Antwort von: {{names}}',
    readOnly: 'Nur lesen: Inhalte schreiben die Assistenten.',
    reopenedOnce: 'Einmal wiedereröffnet'
  },
  approvals: {
    title: 'Freigaben',
    lead: 'Vorschläge deines Assistenten, die auf deine Entscheidung warten.',
    empty: 'Nichts wartet auf dich.'
  },
  assistants: {
    title: 'Assistenten',
    lead: 'Wobei die Assistenten deiner Kolleg:innen helfen können. Das pflegt jede:r in der eigenen persona.md.',
    noProfile: 'Noch kein Profil veröffentlicht.'
  },
  log: {
    title: 'Protokoll',
    lead: 'Alles, was geschrieben wurde, mit vollem Inhalt. Nichts davon lässt sich ändern oder löschen.',
    empty: 'Keine Einträge.',
    category: 'Bereich',
    payload: 'Inhalt',
    ticket: 'Ticket'
  },
  eventCategory: {
    ticket: 'Tickets',
    account: 'Konten',
    admin: 'Verwaltung',
    security: 'Sicherheit',
    activity: 'Aktivität'
  },
  account: {
    title: 'Mein Konto',
    profile: 'Profil',
    name: 'Name',
    language: 'Sprache',
    mails: 'Benachrichtigungen per Mail',
    mail: {
      proposal: 'Ein Vorschlag wartet auf meine Freigabe',
      needsHuman: 'Mein Assistent braucht mich',
      paused: 'Ein Ticket wurde pausiert',
      done: 'Mein Ticket ist erledigt',
      declineDigest: 'Täglich: was mein Assistent abgelehnt hat'
    },
    passkeys: 'Passkeys',
    passkeysLead: 'Damit meldest du dich an und bestätigst Freigaben.',
    addPasskey: 'Passkey hinzufügen',
    removePasskey: 'Entfernen',
    lastPasskey: 'Der letzte Passkey lässt sich nicht entfernen.',
    password: 'Passwort',
    currentPassword: 'Aktuelles Passwort',
    newPassword: 'Neues Passwort',
    changePassword: 'Passwort ändern',
    passwordChanged: 'Passwort geändert.',
    passwordFailed: 'Das hat nicht geklappt. Stimmt das aktuelle Passwort?',
    tokens: 'CLI-Tokens',
    tokensLead: 'Für „ticketai init“ auf deinem Rechner. Ein Token kann nur fragen, ob es Neues gibt, sonst nichts.',
    createToken: 'Token erstellen',
    tokenName: 'Name (z. B. Laptop)',
    tokenOnce: 'Kopiere den Token jetzt. Er wird nur einmal angezeigt.',
    revoke: 'Widerrufen',
    revoked: 'widerrufen',
    expires: 'läuft ab {{date}}',
    connections: 'Verbundene Anwendungen',
    connectionsLead: 'Claude-Code-Sitzungen, die für dich handeln dürfen. Widerrufen beendet den Zugriff sofort.',
    noConnections: 'Keine.',
    granted: 'erlaubt am {{date}}'
  },
  admin: {
    title: 'Verwaltung',
    users: 'Benutzer',
    invites: 'Offene Einladungen',
    invite: 'Einladen',
    inviteLanguage: 'Sprache der Einladung',
    inviteSent: 'Einladung an {{email}} verschickt.',
    inviteLinkManual: 'Mailversand ist aus. Schicke diesen Link selbst (7 Tage gültig):',
    role: 'Rolle',
    roles: {
      admin: 'Admin',
      member: 'Mitglied'
    },
    active: 'aktiv',
    inactive: 'deaktiviert',
    deactivate: 'Deaktivieren',
    reactivate: 'Reaktivieren',
    resend: 'Neu senden',
    revokeInvite: 'Zurückziehen',
    expired: 'abgelaufen',
    pluginVersion: 'Plugin',
    noInvites: 'Keine offenen Einladungen.',
    resetPasskeys: 'Passkeys zurücksetzen',
    resetPasskeysHint: 'Für ein verlorenes Gerät: alle Passkeys weg, Abmeldung überall. Beim nächsten Anmelden richtet die Person einen neuen ein.',
    resetPasskeysConfirm: 'Alle Passkeys von {{name}} entfernen und die Person überall abmelden?'
  },
  oauth: {
    title: '{{client}} mit TicketAI verbinden?',
    signedInAs: 'Angemeldet als {{name}} ({{email}})',
    verifiedBy: 'Die Anwendung wird von {{host}} ausgewiesen.',
    unverified: 'Diese Anwendung ist nicht ausgewiesen.',
    redirectsTo: 'Der Zugang geht an',
    assistant: 'Als dein Assistent darf sie:',
    assistantCan: 'Tickets lesen, anlegen und in deinen Tickets schreiben, vorschlagen, ablehnen und dich fragen.',
    worker: 'Als deine Coding-Session darf sie:',
    workerCan: 'Freigegebene Aufgaben lesen und als begonnen, im Fortschritt oder erledigt melden.',
    never: 'Niemals:',
    cannot: 'etwas freigeben, Konten oder Tokens verwalten. Das geht nur hier im Browser, mit deinem Passkey.',
    revoke: 'Du kannst das jederzeit unter Mein Konto → Verbundene Anwendungen widerrufen.',
    allow: 'Erlauben',
    deny: 'Ablehnen',
    failed: 'Das hat nicht geklappt. Starte die Anmeldung in Claude Code neu.',
    invalid: 'Ungültige Anfrage.',
    unknownClient: 'Eine Anwendung'
  },
  actions: {
    ticket_created: 'Ticket angelegt',
    ticket_message: 'Nachricht',
    ticket_notes: 'Notizen aktualisiert',
    ticket_request_human: 'Frage an den Menschen',
    ticket_propose: 'Vorschlag veröffentlicht',
    ticket_decline: 'Abgelehnt',
    ticket_reopen: 'Wiedereröffnet',
    ticket_cancel: 'Zurückgezogen',
    ticket_close: 'Abgeschlossen',
    ticket_approve: 'Freigegeben',
    ticket_reject: 'Vorschlag abgelehnt',
    ticket_start: 'Arbeit begonnen',
    ticket_progress: 'Fortschritt',
    ticket_done: 'Erledigt gemeldet',
    ticket_pause: 'Pausiert',
    ticket_resume: 'Fortgesetzt',
    ticket_paused: 'Automatisch pausiert',
    auth_signed_in: 'Angemeldet',
    auth_signed_out: 'Abgemeldet',
    mcp_tool_called: 'Assistent/Coding-Session hat ein Werkzeug benutzt'
  },
  mail: {
    greeting: 'Hallo,',
    signature: 'Viele Grüße\nTicketAI · Bechtle PLM',
    invite: {
      subject: 'Einladung zu TicketAI',
      body: '{{inviter}} hat dich zu TicketAI eingeladen, dem Ticket-System für unsere Claude-Assistenten.\n\nÜber diesen Link legst du dein Passwort fest und richtest danach einen Passkey ein (gültig bis {{expires}}):\n{{url}}\n\nWenn du mit dieser Einladung nichts anfangen kannst, ignoriere diese E-Mail einfach.'
    },
    resetPassword: {
      subject: 'TicketAI: Passwort zurücksetzen',
      body: 'Für dein TicketAI-Konto wurde ein neues Passwort angefordert.\n\nÜber diesen Link legst du es fest (eine Stunde gültig):\n{{url}}\n\nWarst du das nicht? Dann ignoriere diese E-Mail – dein Passwort bleibt unverändert.'
    },
    proposal: {
      subject: 'TicketAI {{ticket}}: Vorschlag wartet auf deine Freigabe',
      body: 'Dein Assistent hat zu {{ticket}} „{{title}}“ einen Vorschlag (Version {{version}}) veröffentlicht.\n\nPrüfen und freigeben oder ablehnen:\n{{url}}'
    },
    needsHuman: {
      subject: 'TicketAI {{ticket}}: Dein Assistent braucht dich',
      body: 'Dein Assistent hat zu {{ticket}} „{{title}}“ eine Frage an dich:\n\n{{question}}\n\nAntworte ihm in deiner Assistenten-Session. Das Ticket:\n{{url}}'
    },
    paused: {
      subject: 'TicketAI {{ticket}}: Ticket pausiert',
      body: '{{ticket}} „{{title}}“ wurde pausiert ({{reason}}). Solange es pausiert ist, können die Assistenten nicht weiterschreiben.\n\nFortsetzen kannst du es hier:\n{{url}}'
    },
    done: {
      subject: 'TicketAI {{ticket}}: Erledigt',
      body: '{{ticket}} „{{title}}“ ist erledigt.\n\n{{summary}}\n\nDas Ticket:\n{{url}}'
    },
    declineDigest: {
      subject: 'TicketAI: {{count}} Ticket(s) gestern von deinem Assistenten abgelehnt',
      body: 'Dein Assistent hat gestern diese Tickets abgelehnt. Bitte kurz prüfen, ob das richtig war:\n\n{{lines}}\n\nAlle Tickets:\n{{url}}'
    }
  },
  pausedReason: {
    cap: 'zu viele Nachrichten ohne Fortschritt',
    human: 'manuell pausiert'
  },
  /** Stable API error codes (packages/contract/src/base.ts) → user-facing text. */
  errors: {
    UNAUTHORIZED: 'Bitte melde dich an.',
    FORBIDDEN: 'Dafür fehlt dir die Berechtigung.',
    NOT_FOUND: 'Nicht gefunden.',
    CONFLICT: 'Das wurde inzwischen geändert. Bitte neu laden.',
    RATE_LIMITED: 'Zu viele Anfragen. Bitte kurz warten.',
    PASSKEY_REQUIRED: 'Richte zuerst einen Passkey ein.',
    SECRET_DETECTED: 'Der Text sieht aus, als enthielte er Zugangsdaten.',
    INTERNAL_SERVER_ERROR: 'Auf dem Server ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.',
    BAD_REQUEST: 'Die Eingabe ist ungültig.',
    NETWORK: 'Keine Verbindung zum Server.'
  },
  /** CONFLICT reasons (data.reason) → user-facing text. */
  conflicts: {
    proposal_changed: 'Der Vorschlag wurde inzwischen geändert. Prüfe die neue Version.',
    wrong_status: 'Das geht im aktuellen Status nicht mehr. Bitte neu laden.',
    paused: 'Das Ticket ist pausiert.',
    no_proposal: 'Es gibt keinen Vorschlag.',
    last_admin: 'Der letzte aktive Admin kann nicht entfernt werden.',
    self: 'Du kannst dich nicht selbst deaktivieren.',
    email_taken: 'Mit dieser Adresse gibt es schon ein Konto.',
    invite_open: 'Für diese Adresse gibt es schon eine offene Einladung.',
    too_many_tokens: 'Höchstens 5 aktive CLI-Tokens. Widerrufe zuerst einen.',
    too_many_versions: 'Zu viele Vorschlagsversionen für dieses Ticket.'
  }
} as const;
